import { useEffect, useRef, useState } from 'react';

interface IGifCanvasProps {
  imageUrl: string;
  className?: string;
}

export const GifImage: React.FC<IGifCanvasProps> = ({ imageUrl, className }) => {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isGifLoaded, setIsGifLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const hiddenImgRef = useRef<HTMLImageElement | null>(null);

  useEffect(() => {
    setIsGifLoaded(false);
    setPreviewUrl(null);
    setHasError(false);

    const img = new Image();
    hiddenImgRef.current = img;

    const extractFirstFrame = (): void => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      if (!ctx) {
        return;
      }

      canvas.width = img.naturalWidth || img.width;
      canvas.height = img.naturalHeight || img.height;
      ctx.drawImage(img, 0, 0);

      try {
        const dataUrl = canvas.toDataURL('image/png');
        setPreviewUrl(dataUrl);
      } catch {
        // Cross-origin GIF: preview overlay is unavailable, animated GIF is still shown.
      }
    };

    const handleLoad = (): void => {
      // Извлекаем первый кадр сразу после загрузки, до начала анимации
      requestAnimationFrame(() => {
        extractFirstFrame();
      });
    };

    const handleError = (): void => {
      setHasError(true);
    };

    img.onload = handleLoad;
    img.onerror = handleError;
    img.src = imageUrl;

    return () => {
      img.onload = null;
      img.onerror = null;
    };
  }, [imageUrl]);

  const handleGifLoad = (): void => {
    setIsGifLoaded(true);
  };

  const handleGifError = (): void => {
    setHasError(true);
  };

  if (hasError) {
    return <img src={imageUrl} className={className} alt="" />;
  }

  return (
    <div className={className} style={{ display: 'inline-block' }}>
      <img
        src={imageUrl}
        alt=""
        onLoad={handleGifLoad}
        onError={handleGifError}
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'contain',
        }}
      />
      {previewUrl && !isGifLoaded && (
        <img
          src={previewUrl}
          alt=""
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: 'contain',
            pointerEvents: 'none',
          }}
        />
      )}
    </div>
  );
};
